import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const EMPTY_VALUE = '__tm_empty_value__';

const optionValue = (value) => (String(value ?? '') === '' ? EMPTY_VALUE : String(value));
const actualValue = (value) => (value === EMPTY_VALUE ? '' : value);

const optionsFromChildren = (children) => React.Children.toArray(children)
  .filter((child) => React.isValidElement(child) && child.type === 'option')
  .map((child) => ({
    disabled: Boolean(child.props.disabled),
    label: child.props.children,
    searchLabel: React.Children.toArray(child.props.children).join(' '),
    value: String(child.props.value ?? ''),
  }));

const changeEvent = (name, value) => ({
  currentTarget: { name, type: 'select-one', value },
  target: { name, type: 'select-one', value },
});

const SearchableSelect = ({
  className, disabled, id, name, onChange, options, placeholder, required, searchPlaceholder,
  triggerProps, value,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find((item) => item.value === String(value ?? ''));
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return normalized
      ? options.filter((item) => item.searchLabel.toLowerCase().includes(normalized))
      : options;
  }, [options, query]);

  const selectOption = (nextValue) => {
    onChange?.(changeEvent(name, nextValue));
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); if (!nextOpen) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          name={name}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-required={required || undefined}
          className={cn('h-10 w-full justify-between px-3 font-normal', !selected && 'text-slate-500', className)}
          disabled={disabled}
          onChange={onChange}
          {...triggerProps}
        >
          <span className="truncate">{selected?.label || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] min-w-[14rem] p-1"
      >
        <div className="relative border-b border-slate-200 p-1 pb-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-[calc(50%+2px)] text-slate-400" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-9 pl-9 shadow-none"
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1" role="listbox" aria-label={placeholder}>
          {filtered.map((item) => {
            const active = item.value === String(value ?? '');
            return (
              <button
                key={item.value || EMPTY_VALUE}
                type="button"
                role="option"
                aria-selected={active}
                disabled={item.disabled}
                className="relative flex w-full items-center rounded-md py-2 pl-8 pr-3 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent disabled:opacity-50"
                onClick={() => selectOption(item.value)}
              >
                <Check className={cn('absolute left-2 h-4 w-4', active ? 'opacity-100' : 'opacity-0')} />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
          {!filtered.length && <p className="px-3 py-6 text-center text-sm text-slate-500">No options found.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Controlled shadcn select used by the newer fleet screens. It preserves the
 * former select-like onChange contract so existing form state handlers remain
 * small, while rendering Radix/shadcn controls instead of a native dropdown.
 */
const SelectControl = ({
  children, className, disabled = false, id, name, onChange, required = false,
  searchable = false, searchPlaceholder = 'Search options…', value = '', ...triggerProps
}) => {
  const options = useMemo(() => optionsFromChildren(children), [children]);
  const emptyOption = options.find((item) => item.value === '');
  const placeholder = emptyOption?.label || 'Select an option';

  if (searchable) {
    return (
      <SearchableSelect
        className={className}
        disabled={disabled}
        id={id}
        name={name}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        required={required}
        searchPlaceholder={searchPlaceholder}
        triggerProps={triggerProps}
        value={value}
      />
    );
  }

  return (
    <Select
      name={name}
      value={optionValue(value)}
      onValueChange={(nextValue) => onChange?.(changeEvent(name, actualValue(nextValue)))}
      disabled={disabled}
      required={required}
    >
      <SelectTrigger
        id={id}
        name={name}
        className={className}
        aria-required={required || undefined}
        onChange={onChange}
        {...triggerProps}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent position="popper" sideOffset={4}>
        {options.map((item) => (
          <SelectItem key={item.value || EMPTY_VALUE} value={optionValue(item.value)} disabled={item.disabled}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default SelectControl;
